import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { environment } from "../../environments/environment";

export interface GeneratedOption {
  text: string;
  correct: boolean;
}

export interface GeneratedQuestion {
  /** Present only for questions already saved to the quiz (status "keep"). */
  questionId?: number;
  /** Present only for freshly generated suggestions (status "new"). */
  tempId?: string;
  status?: "keep" | "new";
  questionText: string;
  questionType: string;
  options: GeneratedOption[];
}

export interface GenerateResult {
  existing: GeneratedQuestion[];
  suggestions: GeneratedQuestion[];
}

export interface GenerateRequest {
  /** Content chapter (final quizzes). When set without sectionIds, all of its sections are used. */
  chapterId?: number;
  /** Ids of the sections to use as grounding context (server reads content). */
  sectionIds?: number[];
  count?: number;
  questionType?: string;
  feedback?: {
    avoidQuestions?: string[];
    notes?: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class QuizGenService {
  baseURL: string = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private basePath(courseId: number, chapterId: number, quizId: number): string {
    return `${environment.apiUrl}/courses/${courseId}/chapters/${chapterId}/quizzes/${quizId}/generate`;
  }

  /** Ask Bedrock for multiple-choice suggestions from the chapter's content. */
  async generate(
    courseId: number,
    chapterId: number,
    quizId: number,
    body: GenerateRequest
  ): Promise<GenerateResult> {
    return await lastValueFrom(
      this.http.post<GenerateResult>(
        this.basePath(courseId, chapterId, quizId),
        body
      )
    );
  }

  /** Persist the author-selected questions against the quiz (additive). */
  async publish(
    courseId: number,
    chapterId: number,
    quizId: number,
    questions: GeneratedQuestion[]
  ): Promise<any> {
    return await lastValueFrom(
      this.http.post(`${this.basePath(courseId, chapterId, quizId)}/publish`, {
        questions,
      })
    );
  }
}
